import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, Loader2, Mic, Square, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyDraft = {
  customer_name: "",
  customer_phone: "",
  service_id: "",
  staff_id: "",
  date: "",
  time: "",
};

export default function VoiceBookingDialog({ isPro, isLoading }) {
  const queryClient = useQueryClient();
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [draft, setDraft] = useState(emptyDraft);

  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.get("/services").then((response) => response.data),
    enabled: open && isPro && !isLoading,
  });
  const { data: staff = [] } = useQuery({
    queryKey: ["staff"],
    queryFn: () => api.get("/staff").then((response) => response.data),
    enabled: open && isPro && !isLoading,
  });

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open && recording) {
      recorderRef.current?.stop();
    }
  }, [open, recording]);

  useEffect(() => () => stopTracks(), []);

  const processRecording = async (blob) => {
    setProcessing(true);
    try {
      const audio = new File([blob], "sesli-randevu.webm", { type: blob.type || "audio/webm" });
      const formData = new FormData();
      formData.append("file", audio);
      const { data } = await api.post("/voice/commands/parse", formData);
      setTranscript(data.transcript || "");
      const parsed = data.draft || {};
      setDraft({
        customer_name: parsed.customer_name || "",
        customer_phone: parsed.customer_phone || "",
        service_id: parsed.service_id || "",
        staff_id: parsed.staff_id || "",
        date: parsed.date || "",
        time: parsed.time || "",
      });
      toast.success("Sesli komut taslağa dönüştürüldü");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProcessing(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Tarayıcınız ses kaydını desteklemiyor");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options = MediaRecorder.isTypeSupported("audio/webm")
        ? { mimeType: "audio/webm" }
        : undefined;
      const recorder = new MediaRecorder(stream, options);
      streamRef.current = stream;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stopTracks();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) processRecording(blob);
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (error) {
      toast.error("Mikrofon izni gerekli");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const saveReservation = async () => {
    if (!draft.customer_name || !draft.service_id || !draft.date || !draft.time) {
      toast.error("Ad, hizmet, tarih ve saat alanlarını tamamlayın");
      return;
    }
    setSaving(true);
    try {
      await api.post("/reservations/manual", {
        ...draft,
        staff_id: draft.staff_id || undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success("Randevu oluşturuldu");
      setOpen(false);
      setDraft(emptyDraft);
      setTranscript("");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={
            "-mt-7 flex h-16 w-16 items-center justify-center rounded-full border-4 border-background "
            + "bg-primary text-primary-foreground shadow-lg transition-transform duration-200 hover:-translate-y-1"
          }
          aria-label="Sesli randevu asistanı"
          data-testid="bottom-nav-pro-mic"
        >
          <Mic className="h-6 w-6" />
        </button>
      </DialogTrigger>
      <DialogContent
        className="business-panel-light max-h-[90vh] overflow-y-auto"
        data-testid="voice-booking-dialog"
      >
        {isLoading ? (
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
              Üyelik kontrol ediliyor
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Sesli asistan erişiminiz hazırlanıyor.
            </DialogDescription>
          </DialogHeader>
        ) : !isPro ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-100">
                <Crown className="h-5 w-5 text-amber-400" />
                Sesli Randevu Asistanı
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Konuşarak randevu taslağı oluşturma özelliği Pro üyeliğe özeldir.
              </DialogDescription>
            </DialogHeader>
            <Link to="/panel/abonelik" onClick={() => setOpen(false)}>
              <Button className="mt-3 w-full bg-amber-400 text-slate-950 hover:bg-amber-300" data-testid="voice-upgrade-button">
                Pro’ya Geç
              </Button>
            </Link>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-100">
                <Sparkles className="h-5 w-5 text-amber-400" />
                Sesli Randevu Asistanı
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Müşteri, hizmet, gün ve saati söyleyin; randevu oluşturulmadan önce taslağı kontrol edin.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2 flex justify-center">
              <Button
                size="icon"
                onClick={recording ? stopRecording : startRecording}
                disabled={processing}
                className={
                  "h-20 w-20 rounded-full bg-amber-400 text-slate-950 hover:bg-amber-300 "
                  + "disabled:opacity-60"
                }
                data-testid="voice-record-button"
              >
                {processing ? <Loader2 className="h-7 w-7 animate-spin" /> : recording ? <Square className="h-7 w-7 fill-current" /> : <Mic className="h-7 w-7" />}
              </Button>
            </div>
            <p className="text-center text-xs text-slate-400" data-testid="voice-recording-state">
              {processing ? "Sesiniz çözülüyor…" : recording ? "Kaydediliyor — bitince dokunun" : "Konuşmak için mikrofona dokunun"}
            </p>

            {transcript && (
              <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 text-sm text-slate-300" data-testid="voice-transcript">
                “{transcript}”
              </div>
            )}

            <div className="space-y-3">
              <Input
                value={draft.customer_name}
                onChange={(event) => setDraft((value) => ({ ...value, customer_name: event.target.value }))}
                placeholder="Müşteri adı"
                className="border-slate-700 bg-slate-900 text-slate-100"
                data-testid="voice-customer-name"
              />
              <Input
                value={draft.customer_phone}
                onChange={(event) => setDraft((value) => ({ ...value, customer_phone: event.target.value }))}
                placeholder="Telefon (opsiyonel)"
                className="border-slate-700 bg-slate-900 text-slate-100"
                data-testid="voice-customer-phone"
              />
              <Select
                value={draft.service_id}
                onValueChange={(service_id) => setDraft((value) => ({ ...value, service_id }))}
              >
                <SelectTrigger className="border-slate-700 bg-slate-900 text-slate-100" data-testid="voice-service-select">
                  <SelectValue placeholder="Hizmet seçin" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter((service) => service.active).map((service) => (
                    <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={draft.staff_id || "any"}
                onValueChange={(staff_id) => setDraft((value) => ({ ...value, staff_id: staff_id === "any" ? "" : staff_id }))}
              >
                <SelectTrigger className="border-slate-700 bg-slate-900 text-slate-100" data-testid="voice-staff-select">
                  <SelectValue placeholder="Personel fark etmez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Personel fark etmez</SelectItem>
                  {staff.filter((person) => person.active).map((person) => (
                    <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  value={draft.date}
                  onChange={(event) => setDraft((value) => ({ ...value, date: event.target.value }))}
                  className="border-slate-700 bg-slate-900 text-slate-100"
                  data-testid="voice-date-input"
                />
                <Input
                  type="time"
                  value={draft.time}
                  onChange={(event) => setDraft((value) => ({ ...value, time: event.target.value }))}
                  className="border-slate-700 bg-slate-900 text-slate-100"
                  data-testid="voice-time-input"
                />
              </div>
            </div>

            <Button
              onClick={saveReservation}
              disabled={saving}
              className="mt-2 w-full bg-amber-400 text-slate-950 hover:bg-amber-300"
              data-testid="voice-confirm-booking-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Taslağı Onayla ve Randevu Oluştur"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}